import { lazy, type ComponentType } from "react";

/**
 * `React.lazy` wrapper that survives stale-chunk errors after a deploy.
 *
 * Vite gives every code-split chunk a content hash (e.g.
 * `Dashboard-CupNWu50.js`). When we ship a new build to DO App
 * Platform the old hashed file disappears — replaced by a freshly
 * hashed version. Anyone with the SPA open in a tab still references
 * the old hash via the in-memory `import('/assets/Dashboard-…')`
 * call. Navigation triggers the import → 404 → SPA HTML fallback →
 * browser tries to parse HTML as JS and fires either:
 *   - "Failed to fetch dynamically imported module"
 *   - "'text/html' is not a valid JavaScript MIME type"
 *
 * Reported 2026-05-27: a plain `window.location.reload()` after the
 * first failure wasn't enough. Browsers and intermediate caches
 * (service worker, CDN) can keep serving the *old* `index.html`,
 * which still references the dead chunk hashes — we loop. Fix:
 * - try once, retry once,
 * - on second failure THAT MATCHES THE CHUNK SIGNATURE, force a hard
 *   reload that nukes service-worker + Cache API entries and
 *   appends a cache-buster query string to make absolutely sure
 *   the next `index.html` comes from origin,
 * - guard with sessionStorage so a broken deploy can't ping-pong.
 *
 * Usage: replace `lazy(() => import('@/pages/X'))` with
 * `lazyWithRetry(() => import('@/pages/X'))` everywhere a route is
 * registered. Same shape, same `Suspense` requirements.
 */
const RELOAD_GUARD_KEY = "lilia:chunk-reload-attempted";

function reloadAlreadyAttempted(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
  } catch {
    return false;
  }
}

function markReloadAttempted(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  } catch {
    /* sessionStorage can be denied in private mode — fine */
  }
}

/**
 * Clear the reload guard once the app has successfully booted again.
 * Call from a top-level component's `useEffect` so a future stale
 * chunk after this user reaches the dashboard can still trigger one
 * recovery reload.
 */
export function clearChunkReloadGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Hard-reload that also nukes SW + Cache API entries and
 * appends a cache-buster so intermediate caches return the latest
 * `index.html`. Returns immediately — the location swap is what
 * tears down React's promise chain.
 */
function hardReload(): void {
  if (typeof window === "undefined") return;
  try {
    void (async () => {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
      }
      if ("caches" in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n).catch(() => null)));
      }
    })();
  } catch {
    /* shouldn't sink the reload */
  }
  const url = new URL(window.location.href);
  url.searchParams.set("v", String(Date.now()));
  window.location.replace(url.toString());
}

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = `${err.name}: ${err.message}`;
  return (
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /is not a valid JavaScript MIME type/i.test(msg) ||
    /'text\/html' is not a valid/i.test(msg)
  );
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch {
      // Retry once — covers a transient network blip without nuking
      // the SPA shell.
      try {
        return await factory();
      } catch (secondErr) {
        if (isChunkLoadError(secondErr) && !reloadAlreadyAttempted()) {
          markReloadAttempted();
          hardReload();
        }
        throw secondErr;
      }
    }
  });
}
